import { useEffect, useState } from "react";
import { Button, Modal } from "semantic-ui-react";
import EditPermitTypeRoleSettingsForm, { FormData } from "./EditPermitTypeRoleSettingsForm";
import { FormError } from "../models/FormError";
import { PermitTypeOrgRole } from "../models/PermitTypeOrgRole";
import { PermitRoleSettingsInput } from "../models/PermitRoleSettingsInput";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (orgRoleId: string, permitRoleSettingsInput: PermitRoleSettingsInput | null) => void;
  submitInProgress: boolean;
  submitErrors?: string[];
  permitTypeOrgRole?: PermitTypeOrgRole;
}

const defaultFormData: FormData = {
  useDefault: true,
  isPermitAssignee: false,
  mandatoryOnSubmission: false,
  shouldAuthorizePermit: false,
  authorizationDeclaration: "",
};

const EditPermitTypeRoleSettingsModal = ({
  open,
  onClose,
  onSubmit,
  submitInProgress,
  submitErrors,
  permitTypeOrgRole,
}: Props) => {
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [formErrors, setFormErrors] = useState<FormError>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (open && permitTypeOrgRole) {
      const hasOverride = permitTypeOrgRole.settings !== null;
      const src = hasOverride ? permitTypeOrgRole.settings! : permitTypeOrgRole.orgRole.permitRoleSettings;
      setFormData({
        useDefault: !hasOverride,
        isPermitAssignee: src.isPermitAssignee,
        mandatoryOnSubmission: src.mandatoryOnSubmission,
        shouldAuthorizePermit: src.shouldAuthorizePermit,
        authorizationDeclaration: src.authorizationDeclaration,
      });
    } else {
      setFormData(defaultFormData);
    }
    setFormErrors({});
    setHasSubmitted(false);
  }, [open, permitTypeOrgRole]);

  const validateFormData = (data: FormData): FormError => {
    const formError: FormError = {};

    if (!data.useDefault && data.shouldAuthorizePermit && !data.authorizationDeclaration) {
      formError.authorizationDeclaration = "Please enter authorization declaration";
    }

    return formError;
  };

  const handleSubmit = () => {
    setHasSubmitted(true);

    const validationErrors = validateFormData(formData);
    setFormErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const settingsInput: PermitRoleSettingsInput | null = formData.useDefault
        ? null
        : {
            isPermitAssignee: formData.isPermitAssignee,
            mandatoryOnSubmission: formData.mandatoryOnSubmission,
            shouldAuthorizePermit: formData.shouldAuthorizePermit,
            authorizationDeclaration: formData.authorizationDeclaration,
          };

      onSubmit(permitTypeOrgRole!.orgRole.id, settingsInput);
    }
  };

  const handleFormChange = (data: FormData) => {
    if (hasSubmitted) {
      setFormErrors(validateFormData(data));
    }

    setFormData({ ...data });
  };

  return (
    <Modal open={open} size="small">
      <Modal.Header>Role settings — {permitTypeOrgRole?.orgRole.displayName}</Modal.Header>
      <Modal.Content>
        <Modal.Description>
          <EditPermitTypeRoleSettingsForm
            formData={formData}
            formErrors={formErrors}
            errors={submitErrors}
            onChange={handleFormChange}
          />
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} positive loading={submitInProgress} disabled={submitInProgress}>
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default EditPermitTypeRoleSettingsModal;

